import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ImageZoomMagnifier.module.css';

function useZoomInteractionAllowed() {
  const [allowed, setAllowed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !window.matchMedia('(hover: none), (pointer: coarse)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(hover: none), (pointer: coarse)');
    const sync = () => setAllowed(!mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return allowed;
}

/**
 * Click-to-zoom magnifier for mouse, tap-to-toggle for touch.
 *
 * - Mouse: hover shows zoom-in cursor, click to activate zoom, move to pan
 * - Mouse: leaving the image hides zoom, re-entering shows it again
 * - Mouse: click image again or click anywhere outside to dismiss
 * - Touch: tap to toggle, drag to pan (disabled on coarse / hover-none devices, e.g. phones)
 *
 * The container keeps overflow:hidden so its size never changes.
 * A full-size <img> is absolutely positioned inside and shifted
 * via left/top, with opacity fading in/out.
 */
export default function ImageZoomMagnifier({
  src,
  alt = '',
  zoomFactor = 2.5,
  fadeDuration = 120,
  imageClassName = '',
  wrapperClassName = '',
  enableTouch = true,
  onZoomActivate,
}) {
  const zoomAllowed = useZoomInteractionAllowed();
  const touchEnabled = enableTouch && zoomAllowed;

  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const wrapperRef = useRef(null);
  const baseImgRef = useRef(null);
  const zoomImgRef = useRef(null);
  const rafRef = useRef(0);
  const touchActiveRef = useRef(false);

  useEffect(() => {
    if (zoomAllowed) return undefined;
    setIsActive(false);
    setIsVisible(false);
    touchActiveRef.current = false;
    return undefined;
  }, [zoomAllowed]);

  useEffect(() => {
    if (!isActive) return undefined;

    const onPointerDownOutside = (event) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (!wrapper.contains(event.target)) {
        setIsActive(false);
        setIsVisible(false);
        touchActiveRef.current = false;
      }
    };

    document.addEventListener('pointerdown', onPointerDownOutside);
    return () => document.removeEventListener('pointerdown', onPointerDownOutside);
  }, [isActive]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const move = useCallback(
    (clientX, clientY) => {
      const wrapper = wrapperRef.current;
      const baseImg = baseImgRef.current;
      const zoomImg = zoomImgRef.current;
      if (!wrapper || !baseImg || !zoomImg) return;

      const rect = wrapper.getBoundingClientRect();
      const wrapperWidth = rect.width;
      const wrapperHeight = rect.height;
      if (!wrapperWidth || !wrapperHeight) return;

      const localX = Math.max(0, Math.min(clientX - rect.left, wrapperWidth));
      const localY = Math.max(0, Math.min(clientY - rect.top, wrapperHeight));

      const naturalWidth = baseImg.naturalWidth || wrapperWidth;
      const naturalHeight = baseImg.naturalHeight || wrapperHeight;
      const objectFit = window.getComputedStyle(baseImg).objectFit || 'fill';
      const wrapperAspect = wrapperWidth / wrapperHeight;
      const imageAspect = naturalWidth / naturalHeight || wrapperAspect;

      let displayedWidth = wrapperWidth;
      let displayedHeight = wrapperHeight;
      let offsetX = 0;
      let offsetY = 0;

      if (objectFit === 'contain' || objectFit === 'scale-down') {
        if (imageAspect > wrapperAspect) {
          displayedWidth = wrapperWidth;
          displayedHeight = wrapperWidth / imageAspect;
          offsetY = (wrapperHeight - displayedHeight) / 2;
        } else {
          displayedHeight = wrapperHeight;
          displayedWidth = wrapperHeight * imageAspect;
          offsetX = (wrapperWidth - displayedWidth) / 2;
        }
      } else if (objectFit === 'cover') {
        if (imageAspect > wrapperAspect) {
          displayedHeight = wrapperHeight;
          displayedWidth = wrapperHeight * imageAspect;
          offsetX = (wrapperWidth - displayedWidth) / 2;
        } else {
          displayedWidth = wrapperWidth;
          displayedHeight = wrapperWidth / imageAspect;
          offsetY = (wrapperHeight - displayedHeight) / 2;
        }
      }

      const imageX = Math.max(0, Math.min(localX - offsetX, displayedWidth));
      const imageY = Math.max(0, Math.min(localY - offsetY, displayedHeight));
      const normalizedX = displayedWidth ? imageX / displayedWidth : 0;
      const normalizedY = displayedHeight ? imageY / displayedHeight : 0;

      const zoomWidth = displayedWidth * zoomFactor;
      const zoomHeight = displayedHeight * zoomFactor;
      const overflowX = Math.max(0, zoomWidth - displayedWidth);
      const overflowY = Math.max(0, zoomHeight - displayedHeight);

      zoomImg.style.width = `${zoomWidth}px`;
      zoomImg.style.height = `${zoomHeight}px`;
      zoomImg.style.left = `${offsetX - normalizedX * overflowX}px`;
      zoomImg.style.top = `${offsetY - normalizedY * overflowY}px`;
    },
    [zoomFactor],
  );

  const scheduleMove = useCallback(
    (clientX, clientY) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => move(clientX, clientY));
    },
    [move],
  );

  const onMouseEnter = useCallback(
    (e) => {
      if (!isActive) return;
      setIsVisible(true);
      move(e.clientX, e.clientY);
    },
    [isActive, move],
  );

  const onMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      if (!isActive) return;
      if (!isVisible) setIsVisible(true);
      scheduleMove(e.clientX, e.clientY);
    },
    [isActive, isVisible, scheduleMove],
  );

  const onMouseDown = useCallback(
    (e) => {
      if (!zoomAllowed) return;
      e.preventDefault();
      if (isActive) {
        setIsActive(false);
        setIsVisible(false);
        return;
      }
      setIsActive(true);
      setIsVisible(true);
      move(e.clientX, e.clientY);
      if (onZoomActivate) onZoomActivate();
    },
    [isActive, move, zoomAllowed, onZoomActivate],
  );

  const onTouchStart = useCallback(
    (e) => {
      if (!touchEnabled) return;
      e.preventDefault();
      if (touchActiveRef.current) {
        touchActiveRef.current = false;
        setIsActive(false);
        setIsVisible(false);
      } else {
        touchActiveRef.current = true;
        setIsActive(true);
        setIsVisible(true);
        const t = e.touches[0] || e.changedTouches[0];
        move(t.clientX, t.clientY);
        if (onZoomActivate) onZoomActivate();
      }
    },
    [move, touchEnabled, onZoomActivate],
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!touchEnabled) return;
      e.preventDefault();
      const t = e.touches[0] || e.changedTouches[0];
      scheduleMove(t.clientX, t.clientY);
    },
    [scheduleMove, touchEnabled],
  );

  const onTouchEnd = useCallback((e) => {
    if (!touchEnabled) return;
    e.preventDefault();
    if (touchActiveRef.current) {
      touchActiveRef.current = false;
      setIsActive(false);
      setIsVisible(false);
    }
  }, [touchEnabled]);

  const wrapperClass = [
    styles.wrapper,
    isActive ? styles.wrapperActive : '',
    !zoomAllowed ? styles.wrapperStatic : '',
    wrapperClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={wrapperRef}
      className={wrapperClass}
      onMouseEnter={zoomAllowed ? onMouseEnter : undefined}
      onMouseLeave={zoomAllowed ? onMouseLeave : undefined}
      onMouseMove={zoomAllowed ? onMouseMove : undefined}
      onMouseDown={zoomAllowed ? onMouseDown : undefined}
      onTouchStart={touchEnabled ? onTouchStart : undefined}
      onTouchMove={touchEnabled ? onTouchMove : undefined}
      onTouchEnd={touchEnabled ? onTouchEnd : undefined}
    >
      <img
        ref={baseImgRef}
        src={src}
        alt={alt}
        width={1120}
        height={752}
        loading="lazy"
        className={`${styles.img} ${imageClassName}`}
        draggable={false}
      />

      <img
        ref={zoomImgRef}
        src={src}
        alt=""
        role="presentation"
        className={`${styles.zoomImg} ${isVisible ? styles.zoomImgActive : ''}`}
        style={{
          transitionDuration: `${fadeDuration}ms`,
        }}
        draggable={false}
      />
    </div>
  );
}
